"use client";

import { newsHref } from "@/lib/news-image";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage, getInitials } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SaveButton from "@/components/save-button";
import { QRShareButton } from "@/components/ui/qr-share-button";
import { SponsorButton } from "@/components/payment/sponsor-button";
import { useToast } from "@/hooks/use-toast";
import { useAdmin } from "@/hooks/use-admin";
import { useAuth } from "@/contexts/auth-context";
import {
  ArrowLeft, Globe, Building, Music2, Users, Calendar, MapPin,
  Newspaper, Video, Edit3, Eye, EyeOff, Save, X, Loader2,
  RefreshCw, PlayCircle, AppWindow,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { ItemArtwork } from "@/components/marketplace/item-artwork";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import React, { useState, useEffect, use, useCallback } from "react";
import { db } from "@/lib/firebase";
import {
  collection, getDocs, getDoc, doc, query, where, orderBy, limit, updateDoc,
} from "firebase/firestore";
import { getThumbnailUrl } from "@/lib/youtube";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import { isPlaceholderImage } from "@/lib/image-helpers";

interface LabelData {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  longDescription?: string;
  logoUrl?: string;
  imageUrl?: string;
  coverImageUrl?: string;
  bannerUrl?: string;
  foundedYear?: number;
  location?: { city?: string; country?: string };
  genres?: string[];
  socialLinks?: Record<string, string>;
  founders?: { name: string; role: string }[];
  status?: string;
  /** Hidden from the Labels list, search and coπ (the page still opens). */
  adminHidden?: boolean;
  /** 'private' = not public at all: the page is "not found" for everyone but
   *  admins and the label's own people (LUV, Gordon 2026-11-03). */
  privacy?: string;
  [key: string]: any;
}

function LabelEditDialog({ label, open, onClose, onSaved }: {
  label: LabelData; open: boolean; onClose: () => void; onSaved: (d: LabelData) => void;
}) {
  const [data, setData] = useState<LabelData>({ ...label });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  useEffect(() => { setData({ ...label }); }, [label]);

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...rest } = data;
      const clean = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined));
      await updateDoc(doc(db, "labels", id), { ...clean, updatedAt: new Date().toISOString() });
      toast({ title: "Label updated" });
      onSaved(data);
      onClose();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Label</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Name</Label><Input value={data.name || ''} onChange={e => setData(d => ({ ...d, name: e.target.value }))} /></div>
          <div><Label>Slug</Label><Input value={data.slug || ''} onChange={e => setData(d => ({ ...d, slug: e.target.value }))} /></div>
          {/* Edits the text the page shows: an imported longDescription won over
              description, so edits here never appeared (Gordon 2026-11-02: "1991 in
              the editor, 1992 on the page"). Both are saved as the same text. */}
          <div><Label>Description</Label><Textarea value={data.longDescription || data.description || ''} onChange={e => setData(d => ({ ...d, description: e.target.value, longDescription: e.target.value }))} rows={5} /></div>
          <div><Label>Logo URL</Label><Input value={data.logoUrl || data.imageUrl || ''} onChange={e => setData(d => ({ ...d, logoUrl: e.target.value, imageUrl: e.target.value }))} /></div>
          <div><Label>Cover / Banner URL</Label><Input value={data.coverImageUrl || data.bannerUrl || ''} onChange={e => setData(d => ({ ...d, coverImageUrl: e.target.value, bannerUrl: e.target.value }))} /></div>
          <div><Label>Founded Year</Label><Input type="number" value={data.foundedYear || ''} onChange={e => setData(d => ({ ...d, foundedYear: parseInt(e.target.value) || undefined }))} /></div>
          <div><Label>Location</Label>
            <div className="flex gap-2">
              <Input placeholder="City" value={data.location?.city || ''} onChange={e => setData(d => ({ ...d, location: { ...d.location, city: e.target.value } }))} />
              <Input placeholder="Country" value={data.location?.country || ''} onChange={e => setData(d => ({ ...d, location: { ...d.location, country: e.target.value } }))} />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={save} className="flex-1" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save
            </Button>
            <Button variant="outline" onClick={onClose} disabled={saving}><X className="h-4 w-4 mr-1" />Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LabelPage({ params }: { params: Promise<{ slug: string }> }) {
  const resolvedParams = use(params);
  const { isSuperAdmin, isAdmin } = useAdmin();
  const { user } = useAuth();
  const { toast } = useToast();

  const [label, setLabel] = useState<LabelData | null>(null);
  const [artists, setArtists] = useState<any[]>([]);
  const [releases, setReleases] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [news, setNews] = useState<any[]>([]);
  // Apps = the label's marketplace items: plug-ins, web instruments, software
  // (Gordon 2026-11-02: "add apps tab in kretz page").
  const [apps, setApps] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const t = useTranslations("labels");
  const tm = useTranslations("marketplace");
  const tCommon = useTranslations("common");
  const [editLabelOpen, setEditLabelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("artists");
  const [isLabelUser, setIsLabelUser] = useState(false);

  const isManager = isSuperAdmin || isAdmin || isLabelUser;

  const loadLabel = useCallback(async () => {
    setIsLoading(true);
    try {
      // ── 1. Resolve label document ──────────────────────────────────────────
      let labelData: LabelData | null = null;
      const directSnap = await getDoc(doc(db, "labels", resolvedParams.slug));
      if (directSnap.exists()) {
        labelData = { id: directSnap.id, ...directSnap.data() } as LabelData;
      } else {
        const q = query(collection(db, "labels"), where("slug", "==", resolvedParams.slug), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) labelData = { id: snap.docs[0].id, ...snap.docs[0].data() } as LabelData;
      }
      setLabel(labelData);
      if (!labelData) return;

      if (user?.uid) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const ud = userDoc.data();
          if (ud?.labelId === labelData.id || ud?.labelName === labelData.name) setIsLabelUser(true);
        }
      }

      const lid = labelData.id;
      const lname = labelData.name;

      // ── 2. Fetch artists FIRST — their IDs/names power the rest ───────────
      let artistDocs: any[] = [];
      try {
        const snap = await getDocs(query(collection(db, "artists"), where("labelId", "==", lid), limit(100)));
        artistDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setArtists(artistDocs);
      } catch { setArtists([]); }

      const artistIds   = artistDocs.map((a: any) => a.id).filter(Boolean);
      const artistNames = artistDocs.map((a: any) => a.name).filter(Boolean);

      // Firestore 'in' queries are limited to 30 values — chunk helper
      // <T,> trailing comma prevents TSX from parsing it as a JSX tag
      const chunk30 = <T,>(arr: T[]): T[][] => {
        const out: T[][] = [];
        for (let i = 0; i < arr.length; i += 30) out.push(arr.slice(i, i + 30));
        return out;
      };

      // Merge multiple QuerySnapshot results into a dedup Map keyed by doc ID
      const mergeSnaps = (map: Map<string, any>, snaps: (any | null)[]) => {
        for (const snap of snaps) {
          snap?.docs?.forEach((d: any) => {
            if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() });
          });
        }
      };

      // ── 3. Releases ────────────────────────────────────────────────────────
      const loadReleases = async () => {
        const found = new Map<string, any>();

        // Direct label linkage
        mergeSnaps(found, await Promise.all([
          getDocs(query(collection(db, "releases"), where("labelId", "==", lid), limit(100))).catch(() => null),
          getDocs(query(collection(db, "releases"), where("labelName", "==", lname), limit(100))).catch(() => null),
        ]));

        // Via label's artists — artist.docId, artistId, artist.name
        if (artistIds.length > 0) {
          await Promise.all([
            ...chunk30(artistIds).map(c =>
              getDocs(query(collection(db, "releases"), where("artist.docId", "in", c), limit(100))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
            ...chunk30(artistIds).map(c =>
              getDocs(query(collection(db, "releases"), where("artistId", "in", c), limit(100))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
            ...chunk30(artistNames).map(c =>
              getDocs(query(collection(db, "releases"), where("artist.name", "in", c), limit(100))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
          ]);
        }

        return Array.from(found.values()).sort((a, b) => {
          const ta = a.releaseDate?.toDate?.()?.getTime() || new Date(a.releaseDate || 0).getTime();
          const tb = b.releaseDate?.toDate?.()?.getTime() || new Date(b.releaseDate || 0).getTime();
          return tb - ta;
        });
      };

      // ── 4. Videos ──────────────────────────────────────────────────────────
      const loadVideos = async () => {
        const found = new Map<string, any>();

        // Direct label linkage
        mergeSnaps(found, await Promise.all([
          getDocs(query(collection(db, "videos"), where("labelId", "==", lid), limit(100))).catch(() => null),
          getDocs(query(collection(db, "videos"), where("labelName", "==", lname), limit(100))).catch(() => null),
        ]));

        // Via label's artists — artist.docId (EntityRef), artistId (flat), artist.name
        if (artistIds.length > 0) {
          await Promise.all([
            ...chunk30(artistIds).map(c =>
              getDocs(query(collection(db, "videos"), where("artist.docId", "in", c))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
            ...chunk30(artistIds).map(c =>
              getDocs(query(collection(db, "videos"), where("artistId", "in", c))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
            ...chunk30(artistNames).map(c =>
              getDocs(query(collection(db, "videos"), where("artist.name", "in", c))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
          ]);
        }

        return Array.from(found.values()).sort((a, b) => {
          const ta = a.createdAt?.toDate?.()?.getTime() || 0;
          const tb = b.createdAt?.toDate?.()?.getTime() || 0;
          return tb - ta;
        });
      };

      // ── 5. News ────────────────────────────────────────────────────────────
      const loadNews = async () => {
        const found = new Map<string, any>();

        mergeSnaps(found, await Promise.all([
          getDocs(query(collection(db, "news"), where("labelId", "==", lid), limit(50))).catch(() => null),
          getDocs(query(collection(db, "news"), where("labelName", "==", lname), limit(50))).catch(() => null),
        ]));

        if (artistIds.length > 0) {
          await Promise.all([
            ...chunk30(artistIds).map(c =>
              getDocs(query(collection(db, "news"), where("artistId", "in", c), limit(50))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
            ...chunk30(artistNames).map(c =>
              getDocs(query(collection(db, "news"), where("artistName", "in", c), limit(50))).catch(() => null)
                .then(s => mergeSnaps(found, [s]))
            ),
          ]);
        }

        return Array.from(found.values()).sort((a, b) => {
          const ta = a.publishedAt?.toDate?.()?.getTime() || new Date(a.publishedAt || 0).getTime();
          const tb = b.publishedAt?.toDate?.()?.getTime() || new Date(b.publishedAt || 0).getTime();
          return tb - ta;
        });
      };

      // ── 6. Apps — marketplace items under this label (brand.labelId, or the
      //       item's own labelId); only active, visible ones ─────────────────
      const loadApps = async () => {
        const found = new Map<string, any>();
        mergeSnaps(found, await Promise.all([
          getDocs(query(collection(db, "item"), where("brand.labelId", "==", lid), limit(200))).catch(() => null),
          getDocs(query(collection(db, "item"), where("labelId", "==", lid), limit(200))).catch(() => null),
        ]));
        return Array.from(found.values())
          .filter((i: any) => !i.adminHidden && !i.hidden && (i.status ?? "active") === "active")
          .sort((a: any, b: any) => String(a.title || "").localeCompare(String(b.title || "")));
      };

      // ── 7. Run content fetches in parallel (artists already done) ──────────
      const [r, v, n, a] = await Promise.all([loadReleases(), loadVideos(), loadNews(), loadApps()]);
      setReleases(r);
      setVideos(v);
      setNews(n);
      setApps(a);

    } catch (err) {
      console.error("Error loading label:", err);
    } finally {
      setIsLoading(false);
    }
  }, [resolvedParams.slug, user?.uid]);

  useEffect(() => { loadLabel(); }, [loadLabel]);

  // Only tabs with something in them are shown (Gordon 2026-11-02: "all tabs
  // that are empty don't show them until content inside"); the first one opens.
  const tabs = [
    { key: "artists", icon: Users, count: artists.length },
    { key: "releases", icon: Music2, count: releases.length },
    { key: "apps", icon: AppWindow, count: apps.length },
    { key: "videos", icon: Video, count: videos.length },
    { key: "news", icon: Newspaper, count: news.length },
  ].filter(tab => tab.count > 0);
  const tabKeys = tabs.map(tab => tab.key).join(",");
  useEffect(() => {
    if (tabs.length && !tabs.some(tab => tab.key === activeTab)) setActiveTab(tabs[0].key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabKeys]);

  const toggleHidden = async () => {
    if (!label) return;
    const next = !label.adminHidden;
    try {
      // Showing it again also makes a private label public again.
      const patch = next ? { adminHidden: true } : { adminHidden: false, privacy: "public" };
      await updateDoc(doc(db, "labels", label.id), { ...patch, updatedAt: new Date().toISOString() });
      setLabel(l => l ? { ...l, ...patch } : l);
      toast({ title: next ? "Label hidden" : "Label visible" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const removeContent = async (collection: string, itemId: string, title: string, msg: string, setter: React.Dispatch<React.SetStateAction<any[]>>) => {
    if (!confirm(`Remove "${title}" from this label?`)) return;
    try {
      await updateDoc(doc(db, collection, itemId), { labelId: '', labelName: '', updatedAt: new Date().toISOString() });
      setter(p => p.filter((i: any) => i.id !== itemId));
      toast({ title: msg });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const formatDate = (ts: any) => {
    if (!ts) return '';
    try { const d = ts?.toDate ? ts.toDate() : new Date(ts); return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return ''; }
  };

  if (isLoading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Building className="h-12 w-12 text-muted-foreground animate-pulse" />
    </div>
  );

  if (!label || (label.privacy === "private" && !isManager)) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Building className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Label not found</h2>
        <Link href="/feed"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sticky breadcrumb */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-[50px] z-30">
        <div className="w-full px-page py-3 flex items-center gap-3">
          <Breadcrumbs className="min-w-0 flex-1" items={[
            { navKey: 'labels', href: '/labels', icon: Building },
            { label: label.name, icon: Building },
          ]} />
          {isManager && (
            <div className="ml-auto flex items-center gap-1.5">
              {label.adminHidden && <Badge variant="destructive" className="text-[10px]">Hidden</Badge>}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditLabelOpen(true)} title="Edit label"><Edit3 className="h-3.5 w-3.5" /></Button>
              {isSuperAdmin && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleHidden} title={label.adminHidden ? "Show" : "Hide"}>
                  {label.adminHidden ? <EyeOff className="h-3.5 w-3.5 text-amber-400" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={loadLabel} title="Refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-page pt-4 pb-2">
        {/* Header — phones: logo + name/facts on one row, the buttons on their own row below (Gordon 2026-11-02). */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Avatar className="h-20 w-20 border-2 border-border rounded-xl shadow flex-shrink-0">
            <AvatarImage src={label.logoUrl || label.imageUrl} alt={label.name} />
            <AvatarFallback className="text-2xl rounded-xl" name={label.name}>{getInitials(label.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold truncate">{label.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
              {label.foundedYear && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Est. {label.foundedYear}</span>}
              {label.location?.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{label.location.city}{label.location.country ? `, ${label.location.country}` : ''}</span>}
              {artists.length > 0 && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{t("artistsCount", { count: artists.length })}</span>}
              {releases.length > 0 && <span className="flex items-center gap-1"><Music2 className="h-3.5 w-3.5" />{t("releasesCount", { count: releases.length })}</span>}
              {apps.length > 0 && <span className="flex items-center gap-1"><AppWindow className="h-3.5 w-3.5" />{t("appsCount", { count: apps.length })}</span>}
            </div>
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-shrink-0">
            <SaveButton itemId={label.id} itemType="item" title={label.name} subtitle="Label" imageUrl={label.logoUrl || label.imageUrl || ''} link={`/labels/${label.slug || label.id}`} />
            <QRShareButton title={label.name} subtitle="Label" imageUrl={label.logoUrl || label.imageUrl || ""} />
            {/* Support this label — PWYW. No Connect account on the label doc
                yet, so it settles to the platform for manual payout (same
                fallback SponsorButton already uses for any recipient-less case). */}
            <SponsorButton
              kind="sponsor"
              entityType="label"
              entityId={label.id}
              entityTitle={label.name}
              coverArtUrl={label.logoUrl || label.imageUrl || ''}
              label="Support"
            />
          </div>
        </div>

        {(label.longDescription || label.description) && (
          <p className="mt-3 text-muted-foreground leading-relaxed whitespace-pre-line max-w-3xl">
            {label.longDescription || label.description}
          </p>
        )}
        {label.genres && label.genres.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {label.genres.map((g: string) => <Badge key={g} variant="outline">{g}</Badge>)}
          </div>
        )}
        {label.socialLinks && Object.keys(label.socialLinks).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.entries(label.socialLinks).map(([p, u]) => u ? (
              <a key={p} href={u as string} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1 capitalize"><Globe className="h-3 w-3" />{p}</Button>
              </a>
            ) : null)}
          </div>
        )}

        {/* ── TABS — only the ones with content ── */}
        {tabs.length > 0 && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-5">
          <TabsList className="flex w-full mb-6">
            {tabs.map(({ key, icon: Icon, count }) => (
              <TabsTrigger key={key} value={key} className="flex-1 gap-1 text-xs sm:text-sm">
                <Icon className="h-3.5 w-3.5" /><span>{t(`tab.${key}`)}</span>
                <span className="ml-1 text-2xs rounded-full bg-primary px-1.5 text-primary-foreground">{count}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Apps — plug-ins, web instruments and software */}
          <TabsContent value="apps">
            <div className="grid grid-cols-2 gap-3 pb-12 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {apps.map((item: any) => {
                const free = item.pricingModel === "free";
                const pwyw = item.pricingModel === "pay_what_you_want" || item.pricingModel === "pwyw";
                return (
                  <Link key={item.id} href={`/marketplace/${item.id}`} className="group block">
                    <div className="aspect-square overflow-hidden rounded-[var(--thumbnail-radius,0px)] bg-muted">
                      <ItemArtwork item={item} className="transition-transform duration-300 group-hover:scale-105" />
                    </div>
                    <p className="mt-1.5 truncate text-sm font-semibold group-hover:underline" title={item.title}>{item.title}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {pwyw ? tm("nameYourPrice") : free ? tCommon("free") : typeof item.price === "number"
                        ? new Intl.NumberFormat(undefined, { style: "currency", currency: (item.currency || "EUR").toUpperCase() }).format(item.price)
                        : ""}
                    </p>
                  </Link>
                );
              })}
            </div>
          </TabsContent>

          {/* Artists */}
          <TabsContent value="artists">
            {artists.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">No artists linked to this label yet.</p>
                {isManager && <p className="text-xs mt-1 opacity-60">Link artists via Super Admin → Artists → Edit Artist</p>}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 pb-12">
                {artists.map((artist: any) => (
                  <div key={artist.id} className="group relative">
                    <Link href={`/artists/${artist.slug || artist.id}`} className="block text-center">
                      <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-muted mb-1 group-hover:ring-2 ring-primary transition-all">
                        {artist.imageUrl && !isPlaceholderImage(artist.imageUrl) ? (
                          <Image src={artist.imageUrl} alt={artist.name} fill className="object-cover" />
                        ) : (
                          <GenerativeArtistAvatar name={artist.name} size={160} className="absolute inset-0 w-full h-full" animate />
                        )}
                      </div>
                      <p className="text-xs font-medium truncate leading-tight">{artist.name}</p>
                      {artist.genres?.length > 0 && <p className="text-[10px] text-muted-foreground truncate">{artist.genres[0]}</p>}
                    </Link>
                    {isManager && (
                      <button className="absolute top-0.5 right-0.5 h-5 w-5 rounded bg-destructive/80 text-white hidden group-hover:flex touch:!flex items-center justify-center"
                        onClick={async () => {
                          if (!confirm(`Remove "${artist.name}" from this label?`)) return;
                          await updateDoc(doc(db, "artists", artist.id), { labelId: '', updatedAt: new Date().toISOString() });
                          setArtists(p => p.filter((a: any) => a.id !== artist.id));
                          toast({ title: `"${artist.name}" removed` });
                        }}><X className="h-3 w-3" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Releases */}
          <TabsContent value="releases">
            {releases.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Music2 className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">No releases linked to this label yet.</p>
                {isManager && <p className="text-xs mt-1 opacity-60">Set labelId/labelName on releases via Super Admin → Releases</p>}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 pb-12">
                {releases.map((r: any) => (
                  <div key={r.id} className="group relative">
                    <Link href={`/music/${r.id}`}>
                      <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-colors">
                        <div className="relative aspect-square bg-muted">
                          {r.coverArtUrl ? <Image src={r.coverArtUrl} alt={r.title} fill className="object-cover" /> : <div className="absolute inset-0 flex items-center justify-center"><Music2 className="h-8 w-8 text-muted-foreground/20" /></div>}
                        </div>
                        <CardContent className="p-2.5">
                          <p className="text-xs font-medium truncate">{r.title}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{r.artist?.name || r.artistName || r.artistRef?.name || 'Unknown'}</p>
                          {r.releaseDate && <p className="text-[10px] text-muted-foreground">{String(r.releaseDate).slice(0, 4)}</p>}
                        </CardContent>
                      </Card>
                    </Link>
                    {isManager && (
                      <button className="absolute top-1 right-1 h-5 w-5 rounded bg-destructive/80 text-white hidden group-hover:flex touch:!flex items-center justify-center z-10"
                        onClick={() => removeContent('releases', r.id, r.title, `"${r.title}" removed`, setReleases)}><X className="h-3 w-3" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Videos */}
          <TabsContent value="videos">
            {videos.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Video className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">No videos linked to this label yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-12">
                {videos.map((v: any) => {
                  const thumb = v.thumbnailUrl || v.imageUrl || (v.youtubeId ? getThumbnailUrl(v.youtubeId) : '');
                  return (
                    <div key={v.id} className="group relative">
                      <Link href={`/videos/${v.videoId || v.id}`}>
                        <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-colors">
                          <div className="relative aspect-video bg-muted">
                            {thumb ? <Image src={thumb} alt={v.title} fill className="object-cover" /> : <div className="absolute inset-0 flex items-center justify-center"><Video className="h-8 w-8 text-muted-foreground/20" /></div>}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity"><PlayCircle className="h-10 w-10 text-white" /></div>
                          </div>
                          <CardContent className="p-2.5">
                            <p className="text-xs font-medium line-clamp-2">{v.title}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{v.artistName || v.artist?.name || v.artist || ''}</p>
                          </CardContent>
                        </Card>
                      </Link>
                      {isManager && (
                        <button className="absolute top-1 right-1 h-5 w-5 rounded bg-destructive/80 text-white hidden group-hover:flex touch:!flex items-center justify-center z-10"
                          onClick={() => removeContent('videos', v.videoId || v.id, v.title, `"${v.title}" removed`, setVideos)}><X className="h-3 w-3" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* News */}
          <TabsContent value="news">
            {news.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Newspaper className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">No news linked to this label yet.</p>
                {isManager && <p className="text-xs mt-1 opacity-60">Set labelId/labelName on articles via Super Admin → News</p>}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-12">
                {news.map((article: any) => (
                  <div key={article.id} className="group relative">
                    <Link href={newsHref(article)}>
                      <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-colors">
                        <div className="relative aspect-[16/9] bg-muted">
                          {(article.imageUrl || article.featuredImageUrl) ? <Image src={article.imageUrl || article.featuredImageUrl} alt={article.title} fill className="object-cover group-hover:scale-105 transition-transform duration-300" /> : <div className="absolute inset-0 flex items-center justify-center"><Newspaper className="h-8 w-8 text-muted-foreground/20" /></div>}
                        </div>
                        <CardContent className="p-3">
                          {article.category && <Badge variant="outline" className="text-[10px] mb-1.5 capitalize">{article.category}</Badge>}
                          <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">{article.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">{formatDate(article.publishedAt)}</p>
                        </CardContent>
                      </Card>
                    </Link>
                    {isManager && (
                      <button className="absolute top-1 right-1 h-5 w-5 rounded bg-destructive/80 text-white hidden group-hover:flex touch:!flex items-center justify-center z-10"
                        onClick={() => removeContent('news', article.id, article.title, `"${article.title}" removed`, setNews)}><X className="h-3 w-3" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        )}
      </div>

      {isManager && label && (
        <LabelEditDialog label={label} open={editLabelOpen} onClose={() => setEditLabelOpen(false)} onSaved={updated => setLabel(updated)} />
      )}
    </div>
  );
}
